import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { LoginPage } from './components/auth/LoginPage';
import { ForgotPasswordPage } from './components/auth/ForgotPasswordPage';
import { Dashboard } from './components/dashboard/Dashboard';
import { SMSPage } from './components/sms/SMSPage';
import { NavigationProvider } from './contexts/NavigationContext';
import Sidebar from './components/navigation/Sidebar';
import TopBar from './components/navigation/TopBar';
import QuickLinks from './components/navigation/QuickLinks';
import { User } from './types/user';
import { BroadcastSMS } from './components/sms/BroadcastSMS';
import { RegisterPage } from './components/auth/RegisterPage';
import { SMSCampaignPage } from './components/sms/SMSCampaignPage';
import { TemplatesPage } from './components/templates/TemplatesPage';
import { SMSLogsPage } from './components/sms/SMSLogsPage';
import { MyApplicationsPage } from './components/applications/MyApplicationsPage';
import { NewContactPage } from './components/contacts/NewContactPage';
import { ContactsListPage } from './components/contacts/ContactsListPage';
import { MyGroupsPage } from './components/groups/MyGroupsPage';
import { PackagesPage } from './components/packages/PackagesPage';
import { SuperAdminPage } from './components/logs/SuperAdminPage';
import { AccountPage } from './components/account/AccountPage';
import { NotificationsPage as UserNotificationsPage } from './components/notifications/NotificationsPage';
import UserRoleManagement from './pages/superadmin/UserRoleManagement';
import MessagingManagement from './pages/superadmin/MessagingManagement';
import ContactManagement from './pages/superadmin/ContactManagement';
import TemplateManagement from './pages/superadmin/TemplateManagement';
import ReportingAnalytics from './pages/superadmin/ReportingAnalytics';
import BillingFinancialManagement from './pages/superadmin/BillingFinancialManagement';
import AIAnalytics from './pages/superadmin/AIAnalytics';
import ChatbotIcon from './components/ChatbotIcon';
import SuperAdminSettings from './pages/superadmin/SuperAdminSettings';
import ManageTemplates from './pages/templates/ManageTemplates';
import PromotionalManagement from './pages/superadmin/PromotionalManagement';
import PlatformSecurity from './pages/superadmin/PlatformSecurity';
import DocumentationPage from './pages/documentation/DocumentationPage';
import QuickStartGuidePage from './pages/documentation/QuickStartGuidePage';
import SmsCampaignGuidePage from './pages/documentation/SmsCampaignGuidePage';
import QuickSmsGuidePage from './pages/documentation/QuickSmsGuidePage';
import ContactManagementGuidePage from './pages/documentation/ContactManagementGuidePage';
import TemplateManagementGuidePage from './pages/documentation/TemplateManagementGuidePage';
import SmsLogsReviewPage from './pages/documentation/SmsLogsReviewPage';
import SettingsGuidePage from './pages/documentation/SettingsGuidePage';
import { SMSPreviewPage } from './components/sms/SMSPreviewPage';
import AllocateCreditsPage from './pages/superadmin/AllocateCreditsPage';
import AddCreditsPage from './pages/superadmin/AddCreditsPage';
import CreatePackagePage from './pages/superadmin/CreatePackagePage';
import AddNewUserPage from './pages/superadmin/AddNewUserPage';
import EditUserPage from './pages/superadmin/EditUserPage';
import AddNewRolePage from './pages/superadmin/AddNewRolePage';
import AddNewPermissionPage from './pages/superadmin/AddNewPermissionPage';
import RoutingParametersPage from './pages/superadmin/RoutingParametersPage';
import ManageSubscribers from './pages/superadmin/ManageSubscribers';
import EncryptionConfigPage from './pages/superadmin/EncryptionConfigPage';
import Configure2FAPage from './pages/superadmin/Configure2FAPage';
import AddIPAddressPage from './pages/superadmin/AddIPAddressPage';
import ConfigureDataMaskingPage from './pages/superadmin/ConfigureDataMaskingPage';
import LiveLogsPage from './pages/superadmin/LiveLogsPage';
import SenderIdManagement from './pages/superadmin/SenderIdManagement';
import SmsProvidersPage from './pages/superadmin/SmsProvidersPage';
import ConfigureBackupPage from './pages/superadmin/ConfigureBackupPage';
import CreateGroup from './pages/superadmin/CreateGroup';
import CreateTemplate from './pages/superadmin/CreateTemplate';
import ContactValidation from './pages/superadmin/ContactValidation';
import { TemplateProvider } from './contexts/TemplateContext';
import { GroupProvider } from './contexts/GroupContext';
import { AIProvider } from './contexts/AIContext';
import AIFeaturesAdmin from './pages/superadmin/AIFeaturesAdmin';
import IntegrationsAdmin from './pages/superadmin/IntegrationsAdmin';
import SubscriptionManagement from './pages/superadmin/SubscriptionManagement';
import SuperAdminNotificationsPage from './pages/superadmin/NotificationsPage';
import { createAuthRequestInit } from './utils/auth';
import { API_BASE } from './config/api';

// Helper function to map API role to UserRole
const mapApiRoleToUserRole = (apiRole: string): User['role'] => {
  switch (apiRole) {
    case 'super_admin':
      return 'super_admin';
    case 'admin':
      return 'admin';
    case 'manager':
      return 'manager';
    case 'demo':
      return 'demo';
    case 'user':
    default:
      return 'user';
  }
};

export const App: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token') || 
                    localStorage.getItem('authToken') || 
                    localStorage.getItem('accessToken');
      
      if (token) {
        try {
          // Decode JWT token to get user info (without verification, just for client-side display)
          // The server will verify the token on each API call
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const userId = payload.id;
            const userEmail = payload.email;
            const userRole = payload.role;
            
            // Try to fetch full user details
            const res = await fetch(
              `${API_BASE}/users/${userId}`,
              createAuthRequestInit({}, true)
            );
            
            if (res.ok) {
              const json = await res.json();
              const apiUser = json.data.user;
              const permissions = Array.isArray(json.data.permissions) ? json.data.permissions : [];
              
              // Map API user to local User type
              const mappedUser: User = {
                id: apiUser.id,
                email: apiUser.companyEmail || apiUser.email || userEmail,
                phoneNumber: apiUser.contactNumber,
                role: mapApiRoleToUserRole(apiUser.role || userRole),
                name: `${apiUser.firstName || ''} ${apiUser.lastName || ''}`.trim() || apiUser.companyEmail || userEmail || 'User',
                createdAt: new Date(apiUser.createdAt),
                lastLogin: apiUser.lastLogin ? new Date(apiUser.lastLogin) : new Date(),
                permissions
              };
              
              setUser(mappedUser);
            } else {
              // Token might be invalid, but keep it and let API calls validate it
              console.warn('Could not fetch user details, token may be expired');
            }
          } catch (decodeError) {
            // Token format is invalid, clear it
            console.error('Invalid token format:', decodeError);
            localStorage.removeItem('token');
            localStorage.removeItem('authToken');
            localStorage.removeItem('accessToken');
          }
        } catch (error) {
          console.error('Failed to check auth:', error);
          // Keep token - let first API call validate it
        }
      }
      setIsLoading(false);
    };
    
    checkAuth();
  }, []);

  const handleLogin = async (email: string, password: string) => {
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ message: 'Login failed' }));
        throw new Error(errorData.message || 'Invalid email or password');
      }

      const json = await res.json();
      
      if (json.status === 'success' && json.data) {
        const { token, user: apiUser, permissions: apiPermissions } = json.data;
        
        // Save token to localStorage (try multiple keys for compatibility)
        localStorage.setItem('token', token);
        localStorage.setItem('authToken', token); // Backup key
        
        // Map API user to local User type
        const mappedUser: User = {
          id: apiUser.id,
          email: apiUser.companyEmail || apiUser.email || email,
          phoneNumber: apiUser.contactNumber,
          role: mapApiRoleToUserRole(apiUser.role),
          name: `${apiUser.firstName || ''} ${apiUser.lastName || ''}`.trim() || apiUser.companyEmail || email,
          createdAt: new Date(apiUser.createdAt || Date.now()),
          lastLogin: apiUser.lastLogin ? new Date(apiUser.lastLogin) : new Date(),
          permissions: Array.isArray(apiPermissions) ? apiPermissions : []
        };
        
        setUser(mappedUser);
      } else {
        throw new Error(json.message || 'Login failed');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      throw error; // Re-throw to let LoginPage handle the error display
    }
  };

  const handleLogout = () => {
    // Clear all possible token keys
    localStorage.removeItem('token');
    localStorage.removeItem('authToken');
    localStorage.removeItem('accessToken');
    setUser(null);
  };

  const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    if (!user) {
      return <Navigate to="/" replace />;
    }
    return <>{children}</>;
  };

  const SuperAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    if (!user) {
      return <Navigate to="/" replace />;
    }
    if (user.role !== 'super_admin') {
      return <Navigate to="/dashboard" replace />;
    }
    return <>{children}</>;
  };

  return (
    <BrowserRouter>
      <AIProvider>
        {isLoading ? (
          <div className="min-h-screen flex items-center justify-center bg-gray-100">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading...</p>
            </div>
          </div>
        ) : user ? (
          <NavigationProvider user={user} onLogout={handleLogout}>
            <div className="flex bg-gray-100">
              <Sidebar />
              <div className="flex-1 flex flex-col overflow-hidden">
                <TopBar />
                <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100">
        <Routes>
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/forgot-password" element={<Navigate to="/dashboard" replace />} />
          <Route 
            path="/superadmin/sender-ids" 
            element={
              <SuperAdminRoute>
                <SenderIdManagement />
              </SuperAdminRoute>
            } 
          />
          <Route 
            path="/superadmin/sms-providers" 
            element={
              <SuperAdminRoute>
                <SmsProvidersPage />
              </SuperAdminRoute>
            } 
          />
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                          <Dashboard user={user} />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/sms" 
            element={
              <ProtectedRoute>
                <SMSPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/sms/broadcast" 
            element={
              <ProtectedRoute>
                <BroadcastSMS />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/sms/campaign" 
            element={
              <ProtectedRoute>
                <SMSCampaignPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/templates" 
            element={
              <ProtectedRoute>
                <TemplatesPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/templates/create" 
            element={
              <ProtectedRoute>
                <TemplatesPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/templates/manage" 
            element={
              <ProtectedRoute>
                <ManageTemplates />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/sms/logs" 
            element={
              <ProtectedRoute>
                <SMSLogsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/applications" 
            element={
              <ProtectedRoute>
                <MyApplicationsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/contacts/new" 
            element={
              <ProtectedRoute>
                <NewContactPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/contacts" 
            element={
              <ProtectedRoute>
                <ContactsListPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/contacts/groups" 
            element={
              <ProtectedRoute>
                <MyGroupsPage />
              </ProtectedRoute>
            } 
          />
          <Route path="/packages" element={<PackagesPage />} />
          <Route 
            path="/super-admin" 
            element={
              <SuperAdminRoute>
                <SuperAdminPage />
              </SuperAdminRoute>
            } 
          />
          <Route 
            path="/settings/account" 
            element={
              <ProtectedRoute>
                <AccountPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/settings/notifications" 
            element={
              <ProtectedRoute>
                <UserNotificationsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/superadmin/users-roles" 
            element={
              <SuperAdminRoute>
                <UserRoleManagement />
              </SuperAdminRoute>
            } 
          />
          <Route 
            path="/superadmin/messaging" 
            element={
              <SuperAdminRoute>
                <MessagingManagement />
              </SuperAdminRoute>
            } 
          />
          <Route 
            path="/superadmin/contacts" 
            element={
              <SuperAdminRoute>
                <ContactManagement />
              </SuperAdminRoute>
            } 
          />
          <Route 
            path="/superadmin/contact-validation" 
            element={
              <SuperAdminRoute>
                <ContactValidation />
              </SuperAdminRoute>
            } 
          />
          <Route 
            path="/superadmin/create-group" 
            element={
              <SuperAdminRoute>
                <CreateGroup />
              </SuperAdminRoute>
            } 
          />
          <Route 
            path="/superadmin/templates" 
            element={
              <SuperAdminRoute>
                <TemplateManagement />
              </SuperAdminRoute>
            } 
          />
          <Route 
            path="/superadmin/templates/create" 
            element={
              <SuperAdminRoute>
                <CreateTemplate />
              </SuperAdminRoute>
            } 
          />
          <Route 
            path="/superadmin/analytics" 
            element={
              <SuperAdminRoute>
                <ReportingAnalytics />
              </SuperAdminRoute>
            } 
          />
          <Route 
            path="/superadmin/integrations" 
            element={
              <SuperAdminRoute>
                <IntegrationsAdmin />
              </SuperAdminRoute>
            } 
          />
          <Route 
            path="/superadmin/subscriptions" 
            element={
              <SuperAdminRoute>
                <SubscriptionManagement />
              </SuperAdminRoute>
            } 
          />
          <Route 
            path="/superadmin/billing" 
            element={
              <SuperAdminRoute>
                <BillingFinancialManagement />
              </SuperAdminRoute>
            } 
          />
          <Route 
            path="/superadmin/ai-analytics" 
            element={
              <SuperAdminRoute>
                <AIAnalytics />
              </SuperAdminRoute>
            } 
          />
          <Route 
            path="/superadmin/settings" 
            element={
              <SuperAdminRoute>
                <SuperAdminSettings />
              </SuperAdminRoute>
            } 
          />
          <Route 
            path="/superadmin/promotions" 
            element={
              <SuperAdminRoute>
                <PromotionalManagement />
              </SuperAdminRoute>
            } 
          />
          <Route 
            path="/superadmin/notifications" 
            element={
              <SuperAdminRoute>
                <SuperAdminNotificationsPage />
              </SuperAdminRoute>
            } 
          />
          <Route 
            path="/superadmin/security" 
            element={
              <SuperAdminRoute>
                <PlatformSecurity />
              </SuperAdminRoute>
            } 
          />
          <Route 
            path="/superadmin/security/encryption-config" 
            element={
              <SuperAdminRoute>
                <EncryptionConfigPage />
              </SuperAdminRoute>
            } 
          />
          <Route 
            path="/superadmin/security/configure-2fa" 
            element={
              <SuperAdminRoute>
                <Configure2FAPage />
              </SuperAdminRoute>
            } 
          />
          <Route 
            path="/superadmin/security/add-ip" 
            element={
              <SuperAdminRoute>
                <AddIPAddressPage />
              </SuperAdminRoute>
            } 
          />
          <Route 
            path="/superadmin/security/data-masking" 
            element={
              <SuperAdminRoute>
                <ConfigureDataMaskingPage />
              </SuperAdminRoute>
            } 
          />
          <Route 
            path="/superadmin/security/live-logs" 
            element={
              <SuperAdminRoute>
                <LiveLogsPage />
              </SuperAdminRoute>
            } 
          />
          <Route 
            path="/superadmin/security/configure-backup" 
            element={
              <SuperAdminRoute>
                <ConfigureBackupPage />
              </SuperAdminRoute>
            } 
          />
          <Route 
            path="/documentation" 
            element={
              <ProtectedRoute>
                <DocumentationPage />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/docs/quick-import"
            element={
              <ProtectedRoute>
                <SettingsGuidePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/docs/sms-logs"
            element={
              <ProtectedRoute>
                <SmsLogsReviewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/docs/bulk-sms"
            element={
              <ProtectedRoute>
                <TemplateManagementGuidePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/docs/contacts"
            element={
              <ProtectedRoute>
                <ContactManagementGuidePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/docs/sms-campaign"
            element={
              <ProtectedRoute>
                <SmsCampaignGuidePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/docs/quick-sms"
            element={
              <ProtectedRoute>
                <QuickSmsGuidePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/docs/quick-start"
            element={
              <ProtectedRoute>
                <QuickStartGuidePage />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/sms/preview" 
            element={
              <ProtectedRoute>
                <SMSPreviewPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/superadmin/billing/allocate-credits" 
            element={
              <SuperAdminRoute>
                <AllocateCreditsPage />
              </SuperAdminRoute>
            } 
          />
          <Route 
            path="/superadmin/billing/add-credits" 
            element={
              <SuperAdminRoute>
                <AddCreditsPage />
              </SuperAdminRoute>
            } 
          />
          <Route 
            path="/superadmin/billing/create-package" 
            element={
              <SuperAdminRoute>
                <CreatePackagePage />
              </SuperAdminRoute>
            } 
          />
          <Route 
            path="/superadmin/users-roles/add-user" 
            element={
              <SuperAdminRoute>
                <AddNewUserPage />
              </SuperAdminRoute>
            } 
          />
          <Route 
            path="/superadmin/users-roles/edit/:id" 
            element={
              <SuperAdminRoute>
                <EditUserPage />
              </SuperAdminRoute>
            } 
          />
          <Route 
            path="/superadmin/users-roles/add-role" 
            element={
              <SuperAdminRoute>
                <AddNewRolePage />
              </SuperAdminRoute>
            } 
          />
          <Route 
            path="/superadmin/users-roles/add-permission" 
            element={
              <SuperAdminRoute>
                <AddNewPermissionPage />
              </SuperAdminRoute>
            } 
          />
          <Route 
            path="/superadmin/messaging/routing-parameters" 
            element={
              <SuperAdminRoute>
                <RoutingParametersPage />
              </SuperAdminRoute>
            } 
          />
          <Route 
            path="/superadmin/subscribers" 
            element={
              <SuperAdminRoute>
                <ManageSubscribers />
              </SuperAdminRoute>
            } 
          />
                    <Route 
                      path="/superadmin/ai-features" 
                      element={
                <SuperAdminRoute>
                          <AIFeaturesAdmin />
                </SuperAdminRoute>
                      } 
                    />
                  </Routes>
                </main>
              </div>
            </div>
          </NavigationProvider>
        ) : (
          <Routes>
            <Route path="/" element={<LoginPage onLogin={handleLogin} />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        )}
      </AIProvider>
        {/* <ChatbotIcon /> */}
    </BrowserRouter>
  );
}; 